const CHALLENGE_HASH_KEY = 'challenge';

const normalizeCard = (card, index) => ({
  id: card.id ?? `challenge-card-${index}`,
  question: card.question,
  answer: card.answer,
  incorrect: card.incorrect,
});

const encodeUnicode = (value) => {
  return btoa(unescape(encodeURIComponent(value)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
};

const decodeUnicode = (value) => {
  const padded = value + '==='.slice((value.length + 3) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  return decodeURIComponent(escape(atob(base64)));
};

export const createSeed = () => Math.floor(Date.now() % 2147483647);

const createRandom = (seed) => {
  let value = seed % 2147483647;
  if (value <= 0) value += 2147483646;

  return () => {
    value = (value * 16807) % 2147483647;
    return (value - 1) / 2147483646;
  };
};

export const seededShuffle = (cards, seed) => {
  const random = createRandom(seed);
  const nextCards = cards.map(normalizeCard);

  for (let i = nextCards.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [nextCards[i], nextCards[j]] = [nextCards[j], nextCards[i]];
  }

  return nextCards;
};

export const createChallengePayload = ({ title, topic, difficulty, seed, cards, sourceResult = null }) => ({
  title,
  topic,
  difficulty,
  seed,
  cards: cards.map(normalizeCard),
  sourceResult,
});

export const buildChallengeUrl = (payload) => {
  const encoded = encodeUnicode(JSON.stringify(payload));
  return `${window.location.origin}${window.location.pathname}#${CHALLENGE_HASH_KEY}=${encoded}`;
};

export const parseChallengeHash = (hash = window.location.hash) => {
  if (!hash.startsWith(`#${CHALLENGE_HASH_KEY}=`)) {
    return null;
  }

  try {
    const encoded = hash.slice(CHALLENGE_HASH_KEY.length + 2);
    const parsed = JSON.parse(decodeUnicode(encoded));

    if (!parsed || !Array.isArray(parsed.cards) || parsed.cards.length === 0) {
      return null;
    }

    return {
      ...parsed,
      cards: parsed.cards.map(normalizeCard),
    };
  } catch (error) {
    console.error('Failed to parse challenge hash:', error);
    return null;
  }
};

export const clearChallengeHash = () => {
  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
};

export const formatDuration = (durationMs) => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

export const shareText = async ({ title, text, url }) => {
  if (navigator.share) {
    await navigator.share({ title, text, url });
    return 'Shared successfully.';
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return 'Link copied to clipboard.';
  }

  window.prompt('Copy this link', url);
  return 'Copy the link from the prompt.';
};
